import { ColDef } from 'ag-grid-community'
import columnRenderers, { PLACEHOLDER } from 'components/column-renderers'
import { CSVField } from 'hooks/useExportCSV'
import capitalize from 'lodash/capitalize'
import { AccessCode, EAccessCodeOwnership, EAccessCodeOwnershipLabels } from 'types'
import { formatDate } from 'utils'
import { AccessManagementColumnCode } from '../AccessManagementColumnCode/AccessManagementColumnCode'
import { AccessManagementColumnCodeType } from '../AccessManagementColumnCodeType/AccessManagementColumnCodeType'
import { AccessManagementColumnOwnership } from '../AccessManagementColumnOwnership/AccessManagementColumnOwnership'

export const accessManagementColumns = {
  codeType: {
    headerName: 'Code Type',
    field: 'codeType',
    minWidth: 220,
    cellRenderer: AccessManagementColumnCodeType,
  },
  ownership: {
    headerName: 'Code Ownership',
    minWidth: 210,
    field: 'ownership',
    cellRenderer: AccessManagementColumnOwnership,
  },
  status: {
    headerName: 'Status',
    field: 'status',
    minWidth: 120,
    cellRenderer: columnRenderers.capitalizeRenderer('status'),
  },
  codeValue: {
    headerName: 'Code Value',
    cellRenderer: AccessManagementColumnCode,
    colId: 'code',
    minWidth: 210,
    flex: 1,
  },
  effectiveDate: {
    headerName: 'Effective Date',
    cellRenderer: columnRenderers.date('effectiveDate'),
    minWidth: 270,
    colId: 'effectiveDate',
    flex: 1,
  },
  expiryDate: {
    headerName: 'Expiry Date',
    cellRenderer: columnRenderers.date('expiryDate'),
    minWidth: 240,
    colId: 'expiryDate',
  },
  numberOfUsers: { headerName: 'Number of Users', field: 'numberOfUsers', minWidth: 200 },
  codeCanBeUsed: {
    headerName: 'Code Not Used',
    valueGetter: (v) => (v.data ? v.data.numberOfUsers - v.data.timesUsed : '-'),
    sortable: false,
  },
  timesUsed: { headerName: 'Code Used', field: 'timesUsed' },
  reason: {
    headerName: 'Reason for Code',
    field: 'reason',
    valueGetter: (value) => {
      if (!value.data) return PLACEHOLDER

      return value.data.reason || PLACEHOLDER
    },
    filter: 'agTextColumnFilter',
  },
  organizationName: {
    headerName: 'Organization Name',
    field: 'organizationName',
    minWidth: 220,
    filter: 'agTextColumnFilter',
    valueGetter: (value) => {
      if (!value.data) return PLACEHOLDER
      if (value.data.organizationName) {
        return value.data.organizationName
      }

      return value.data.organization?.name || PLACEHOLDER
    },
  },
  twoDigitsOrg: {
    headerName: 'Two-Digit Organization Number',
    field: 'twoDigitsOrg',
    minWidth: 300,
    valueGetter: (value) => {
      if (!value.data) return PLACEHOLDER
      if (value.data.twoDigitsOrg) {
        return value.data.twoDigitsOrg
      }

      return value.data.organization?.twoDigits || PLACEHOLDER
    },
  },
  organizationType: {
    headerName: 'Organization Type',
    field: 'organizationType',
    minWidth: 220,
    filter: 'agTextColumnFilter',
    valueGetter: (value) => {
      if (!value.data) return PLACEHOLDER
      if (value.data.organizationType) {
        return value.data.organizationType
      }

      return value.data.organization?.type || PLACEHOLDER
    },
  },
  state: {
    headerName: 'Two-Letter State Location of Trainees',
    field: 'state',
    minWidth: 360,
    valueGetter: (value) => {
      if (!value.data) return PLACEHOLDER
      return value.data.state || value.data.specificState || PLACEHOLDER
    },
  },
  specificUnitName: {
    headerName: 'Specific Unit Name',
    field: 'specificUnitName',
    valueGetter: (value) => {
      if (!value.data) return PLACEHOLDER

      return value.data.specificUnitName || PLACEHOLDER
    },
    minWidth: 260,
    filter: 'agTextColumnFilter',
  },
  twoDigitsSpecific: {
    headerName: 'Two-Digit Specific Unit Letter / Number',
    field: 'specificTwoDigits',
    minWidth: 360,
    valueGetter: (value) => {
      if (!value.data) return PLACEHOLDER

      return value.data.specificTwoDigits || PLACEHOLDER
    },
  },
  duration: {
    headerName: 'Access Remaining',
    field: 'duration',
    valueGetter: (value) => {
      if (!value.data) return PLACEHOLDER

      return value.data.duration || PLACEHOLDER
    },
    minWidth: 240,
  },
  careTeamContentSharingEnabled: {
    headerName: 'Sharing Status',
    field: 'careTeamContentSharingEnabled',
    cellRenderer: columnRenderers.checkbox(() => {}),
  },
  careTeamContentSharingEffectiveDate: {
    headerName: 'Sharing Effective Date',
    field: 'careTeamContentSharingEffectiveDate',
    cellRenderer: columnRenderers.date('careTeamContentSharingEffectiveDate'),
    minWidth: 270,
    colId: 'careTeamContentSharingEffectiveDate',
  },
  careTeamContentSharingExpiryDate: {
    headerName: 'Sharing Expiry Date',
    field: 'careTeamContentSharingExpiryDate',
    cellRenderer: columnRenderers.date('careTeamContentSharingExpiryDate'),
    minWidth: 270,
    colId: 'careTeamContentSharingExpiryDate',
  },
} satisfies Record<string, ColDef>

export const institutionalCodeColumns: ColDef[] = [
  accessManagementColumns.codeType,
  accessManagementColumns.ownership,
  accessManagementColumns.status,
  accessManagementColumns.codeValue,
  accessManagementColumns.effectiveDate,
  accessManagementColumns.expiryDate,
  accessManagementColumns.numberOfUsers,
  accessManagementColumns.codeCanBeUsed,
  accessManagementColumns.timesUsed,
  accessManagementColumns.reason,
  accessManagementColumns.organizationName,
  accessManagementColumns.twoDigitsOrg,
  accessManagementColumns.organizationType,
  accessManagementColumns.state,
  accessManagementColumns.specificUnitName,
  accessManagementColumns.twoDigitsSpecific,
  accessManagementColumns.duration,
  accessManagementColumns.careTeamContentSharingEnabled,
  accessManagementColumns.careTeamContentSharingEffectiveDate,
  accessManagementColumns.careTeamContentSharingExpiryDate,
]

/**
 * One promo code per Person of Concern. Status, expiration, user count and the person's name are
 * computed by the backend after the query from the linked Family Users, so they are not sortable:
 * the stored columns of the same name hold stale per-code values, and the name has no column at all.
 */
export const promoCodeColumns: ColDef[] = [
  accessManagementColumns.codeType,
  accessManagementColumns.codeValue,
  accessManagementColumns.effectiveDate,
  { ...accessManagementColumns.status, sortable: false },
  {
    headerName: 'Expiration Date',
    cellRenderer: columnRenderers.date('expiryDate'),
    colId: 'expiryDate',
    minWidth: 180,
    sortable: false,
  },
  { ...accessManagementColumns.numberOfUsers, sortable: false },
  {
    headerName: 'Person of Concern',
    field: 'personOfConcern',
    minWidth: 220,
    sortable: false,
    filter: 'agTextColumnFilter',
  },
]

export const csvInstitutionalCodeColumns: CSVField[] = [
  { label: 'Code Type', value: 'codeType' },
  {
    label: 'Code Ownership',
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    value: ({ ownership }: any) => {
      return EAccessCodeOwnershipLabels[
        (ownership as EAccessCodeOwnership) ?? EAccessCodeOwnership.organizational
      ]
    },
  },
  {
    label: 'Status',
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    value: ({ status }: any) => {
      return status ? capitalize(status) : PLACEHOLDER
    },
  },
  { label: 'Code Value', value: 'codeValue' },
  { label: 'Effective Date', value: 'effectiveDate' },
  { label: 'Expiry Date', value: 'expiryDate' },
  { label: 'Number of Users', value: 'numberOfUsers' },
  { label: 'Code Not Used', value: 'codeCanBeUsed' },
  { label: 'Code Used', value: 'timesUsed' },
  { label: 'Reason for Code', value: 'reason' },
  { label: 'Organization Name', value: 'organizationName' },
  { label: 'Two-Digit Organization Number', value: 'twoDigitsOrg' },
  { label: 'Organization Type', value: 'organizationType' },
  { label: 'Two-Letter State Location of Trainees', value: 'state' },
  { label: 'Specific Unit Name', value: 'specificUnitName' },
  { label: 'Two-Digit Specific Unit Letter / Number', value: 'twoDigitsSpecific' },
  { label: 'Access Code Duration', value: 'duration' },
  {
    label: 'Sharing Status',
    value: ({ careTeamContentSharingEnabled }) => (careTeamContentSharingEnabled ? 'Yes' : 'No'),
  },
  {
    label: 'Sharing Effective Date',
    value: ({ careTeamContentSharingEffectiveDate }) => {
      return careTeamContentSharingEffectiveDate
        ? formatDate(careTeamContentSharingEffectiveDate)
        : PLACEHOLDER
    },
  },
  {
    label: 'Sharing Expiry Date',
    value: ({ careTeamContentSharingExpiryDate }) => {
      return careTeamContentSharingExpiryDate
        ? formatDate(careTeamContentSharingExpiryDate)
        : PLACEHOLDER
    },
  },
]

export const csvPromoCodeColumns: CSVField[] = [
  { label: 'Code Type', value: 'codeType' },
  { label: 'Code Value', value: 'code' },
  { label: 'Effective Date', value: 'effectiveDate' },
  { label: 'Status', value: ({ status }: AccessCode) => capitalize(status) },
  {
    label: 'Expiration Date',
    value: ({ expiryDate }: AccessCode) => (expiryDate ? formatDate(expiryDate) : PLACEHOLDER),
  },
  { label: 'Number of Users', value: 'numberOfUsers' },
  {
    label: 'Person of Concern',
    value: ({ personOfConcern }: AccessCode & { personOfConcern?: string }) =>
      personOfConcern || PLACEHOLDER,
  },
]
